"use client";

import { getUserAuctionItems } from "@/actions/auctions";
import { auctionFilterProps, AuctionItem } from "@/types/auction";
import React, { use, useEffect, useState } from "react";
import useSWR, { mutate } from "swr";
import { useAuctionFilter } from "@/hooks/useAuctionFilter";
import AuctionBarActionsPanelMyPages from "@/components/Auctions/AuctionBarFilter/FilterPanelMyPages";
import { getRecentItemBidsByItemIds } from "@/actions/bids";
import useUserStore from "@/stores/useUserStore";
import { MD5 } from "crypto-js";
import { EXTRA_BID_TIME, REVALIDATE_USER_AUCTIONS_TIME } from "@/lib/auctions";
import AuctionListItems from "@/components/Auctions/AuctionItem/AuctionListItems";
import { getSwedishDate } from "@/lib/utils";
import { SocketProvider } from "@/components/WebSocket/SocketProvider";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

export const defaultFilterPropsMyPagesAuctionItem = {
  pagination: {
    size: 25,
    page: 1,
  },
  sorting: {
    options: [
      {
        id: "itemEndTimeAsc",
        queryKey: "itemEndTime",
        matchKey: "itemEndTime",
        label: "Kortast tid kvar",
        sortOrder: "asc",
        sortBy: "itemEndTime",
      },
      {
        id: "itemEndTimeDesc",
        queryKey: "itemEndTime",
        matchKey: "itemEndTime",
        label: "Nyast",
        sortOrder: "desc",
        sortBy: "itemEndTime",
      },
      {
        id: "itemSortNoAsc",
        queryKey: "itemSortNo",
        matchKey: "itemSortNo",
        label: "Rop",
        sortOrder: "asc",
        sortBy: "itemSortNo",
      },
    ],
    selections: [
      {
        matchKey: "itemEndTime",
        queryKey: "itemEndTime",
        sortOrder: "asc",
        sortBy: "itemEndTime",
      },
    ],
  },
  filtering: {
    selections: [
      {
        queryKey: "isOngoing",
        matchKey: "isOngoing",
        value: "active",
      },
    ],
    options: [
      {
        queryKey: "isOngoing",
        matchKey: "isOngoing",
        label: "Pågående",
        value: "active",
      },
      {
        queryKey: "hasEnded",
        matchKey: "hasEnded",
        label: "Avslutade",
        value: "ended",
      },
      {
        queryKey: "bidWon",
        matchKey: "bidWon",
        label: "Vunna",
        value: "yes",
      },
    ],
  },
};

const BidsAndFavorites = ({ block, blockStyle, extraClass }: BlockProps) => {
  const [displayedItems, setDisplayedItems] = useState<any>([]);
  const auctionFilterProps = useAuctionFilter(
    defaultFilterPropsMyPagesAuctionItem
  );
  const [userAuctionItemBids, setUserAuctionItemBids] = useState<any>(false);

  const { options, setOptions, buildFilterParams } = auctionFilterProps;

  const paginationArgs = options?.pagination as any;
  const sortingArgs = options?.sorting as any;

  const { user, userId, setFetchKeys, fetchKeys } = useUserStore();

  const userFetchKey =
    fetchKeys?.getUserAuctionItems || MD5(`userBids-${userId}`).toString();

  const optionsJson = JSON.stringify(options);

  useEffect(() => {
    console.log('debug1 - trigger fetch!');
    // Set fetch key on init
    const newFetchKey = MD5(`userBids-${userId}-${new Date().getTime()}`).toString();
    setFetchKeys({
      ...fetchKeys,
      getUserAuctionItems: newFetchKey,
    });
  }, [userId]);

  const fetchUserItems = async () => {
    const filter = buildFilterParams();

    const params = {
      offset: (paginationArgs.page - 1) * paginationArgs.size,
      limit: paginationArgs.size,
      sort: {
        [sortingArgs.selections?.[0]?.queryKey]:
          sortingArgs.selections?.[0]?.sortOrder,
      },
      filter: filter,
    };

    const { auctionItems: getAllAuctionItems, totalHits: getTotalHits } =
      await getUserAuctionItems(params, userFetchKey);

    if (!getAllAuctionItems) return [];

    setDisplayedItems(getAllAuctionItems);

    setOptions((prevArgs: auctionFilterProps) => ({
      ...prevArgs,
      pagination: {
        ...prevArgs.pagination,
        total: getTotalHits ? getTotalHits : 0,
      },
    }));

    return getAllAuctionItems || [];
  };

  const {
    data: userItems,
    error,
    isLoading,
    isValidating,
  } = useSWR<AuctionItem[], Error>(
    ["api/getUserBidAndFavorites", optionsJson],
    fetchUserItems,
    {
      refreshInterval: REVALIDATE_USER_AUCTIONS_TIME,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      shouldRetryOnError: true,
    }
  );

  // Fetch bids using SWR for better caching and performance
  const auctionItemIds = userItems?.map((item: any) => item.itemId) || [];
  const { data: bidsData } = useSWR(
    userItems && auctionItemIds.length > 0
      ? ["api/getUserItemBids", auctionItemIds.join(","), userFetchKey]
      : null,
    async () => {
      console.log('debug1 - fetching with fetchKey - ', userFetchKey);

      const { allItemBids } = await getRecentItemBidsByItemIds(auctionItemIds, userFetchKey);
      return allItemBids;
    },
    {
      refreshInterval: REVALIDATE_USER_AUCTIONS_TIME,
      revalidateOnFocus: false,
      dedupingInterval: 10000, // Prevent duplicate requests within 10 seconds
    }
  );

  // Update bids when data changes
  useEffect(() => {
    if (bidsData) {
      setUserAuctionItemBids(bidsData);
    }
  }, [bidsData]);

  return (
    <SocketProvider>
      <div className="flex flex-col space-y-6 w-full" style={blockStyle}>
        <AuctionBarActionsPanelMyPages filterProps={auctionFilterProps} />

        {(isLoading || (isValidating && displayedItems?.length === 0)) ? (
          <div className="space-y-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="rounded-lg overflow-hidden">
                <Skeleton height={300} />
              </div>
            ))}
          </div>
        ) : (
          <AuctionListItems
            visibleItemsData={displayedItems?.length}
            auctionItemBids={userAuctionItemBids}
            filterProps={auctionFilterProps}
            auctionItems={displayedItems}
            isLoading={isLoading}
            error={error}
            showFilter={false}
            searchLayout
          />
        )}
      </div>
    </SocketProvider>
  );
};

export default BidsAndFavorites;
